import fp from 'lodash/fp';
import React, {
    useContext,
    useEffect,
    FC,
    ComponentType,
    ComponentProps,
    useCallback,
} from 'react';
import Measure from 'react-measure';
import { messaging } from '@factorin/container.macro';
import { ConnectedProps, useDispatch } from 'react-redux';

import { connect, useSelector } from 'Modules/core/redux';
import {
    Text,
    Portal,
    CloseButton,
    TransitionComponent,
    TransitionGroup,
    cn,
    contextHOC,
    animation,
    stopPropagation,
    useMemoDeps,
} from 'Modules/ui';

import { messagingActions as actions } from '../../state/actions';
import styles from './index.module.scss';
import {
    MessagingQueueTypes,
    MessagingRootState,
    ModalType,
    MessageBase,
    NotificationType,
} from '../../state/types';


type NotificationProps = {
    hide: () => void;
    notification: NotificationType;
};

const Notification: ComponentType<NotificationProps> = ({ hide, notification: { content } }) => (
    <Measure bounds>
        {({
            measureRef,
            contentRect: {
                bounds: { height },
            },
        }) => (
            <div className={styles.notificationContainer} style={{ height }}>
                <div className={styles.notification} ref={measureRef}>
                    <CloseButton small className={styles.notificationClose} onClick={hide} />
                    <messaging.notificationRenderer content={content} />
                </div>
            </div>
        )}
    </Measure>
);

type ModalHideFunctionType = () => void;
export type ModalContextType = { modal: MessageBase & { hide: ModalHideFunctionType } };

const ModalContextObject = React.createContext<ModalContextType>({} as ModalContextType);
export const ModalContext = ModalContextObject.Consumer;
export const withModalContext = contextHOC(ModalContext);
export const useModalContext = (): ModalContextType => useContext(ModalContextObject);

type WidthFunction = () => string;

type Position = 'center' | 'right' | 'custom';

type ModalProps = {
    modal: ModalType;
};

export type ModalComponent<TProps = {}> = {
    key?: string;
    component: ComponentType<TProps>;
    containerClass?: string;
    modalClass?: string;
    width?: 'auto' | WidthFunction;
    position?: Position;
    verticalOffset?: boolean;
};

export function createModal<TProps>(modal: ModalComponent<TProps>) {
    return Object.assign(modal, { toString: () => modal.key });
}

const useHideAction = () => {
    const queue = useSelector<MessagingQueueTypes[]>('messaging');
    const dispatch = useDispatch();
    const hide = useCallback((modalId: number) =>
        dispatch(actions.hideMessage(modalId)), [dispatch]);
    const hideModal = useCallback(
        (modal: ModalType) => {
            if (messaging.modalTypes[modal.type].hideAction)
                messaging.modalTypes[modal.type].hideAction(modal, dispatch);
            else
                hide(modal.id);
        },
        [dispatch, hide],
    );
    const hideLastModal = useCallback(() => {
        const lastModal = fp.last(queue.filter(m => m.isModal && m.closeable));
        if (lastModal) {
            if (messaging.modalTypes[lastModal.type].hideAction)
                messaging.modalTypes[lastModal.type].hideAction(lastModal, dispatch);
            else
                hide(lastModal.id);
        }
    }, [dispatch, hide, queue]);
    return {
        queue,
        hide,
        hideModal,
        hideLastModal,
    };
};

const Modal: ComponentType<ModalProps> = React.memo(({ modal }) => {
    const { type, props = {} } = modal;
    const { hideModal } = useHideAction();
    if (!messaging.modalTypes[type]) {
        if (process.env.NODE_ENV === 'development')
            console.log('no modal type registered', type);
        return null;
    }

    const {
        component: Component,
        containerClass,
        modalClass,
        width = 'auto',
        position = 'center',
        verticalOffset = true,
    } = messaging.modalTypes[type] as ModalComponent;
    return (
        <div
            className={cn(
                styles.modal,
                position === 'right' ? styles.modalRight : styles.modalCenter,
                modalClass,
            )}
            role='presentation'
            {...modal.closeable && {
                onMouseDown: () => hideModal(modal),
            }}
        >
            <div className={position === 'center' ? 'hcenter' : undefined}>
                {verticalOffset && <div />}
                <div
                    className={cn(styles.modalContainer, containerClass)}
                    onMouseDown={stopPropagation}
                    style={{ width: fp.isFunction(width) ? width() : width }}
                    role='presentation'
                    data-testid={`modal-${type}`}
                >
                    <ModalContextObject.Provider
                        value={{ modal: { ...modal, hide: () => hideModal(modal) } }}
                    >
                        <Component {...props} />
                    </ModalContextObject.Provider>
                    {modal.closeable && (
                        <CloseButton
                            className={styles.modalClose}
                            onClick={() => hideModal(modal)}
                        />
                    )}
                </div>
                {verticalOffset && <div />}
            </div>
        </div>
    );
});

export const modalClassNames = {
    title: styles.modalTitle,
    content: styles.modalContent,
    contentDfa: styles.modalContentDfa,
    buttons: styles.modalButtons,
    button: styles.modalButton,
};

export const connector = connect(
    ({ messaging: queue }: MessagingRootState) => ({
        queue,
    }),
    {
        hide: actions.hideMessage,
    },
);

export type MessagingProps = ConnectedProps<typeof connector>;

export const Messaging: ComponentType = () => {
    const { hide, hideLastModal, queue } = useHideAction();
    useEffect(() => {
        const onKeyDown = e => {
            if (e.key === 'Escape')
                hideLastModal();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [hideLastModal]);

    const [modals, notifications] = useMemoDeps(fp.partition<MessagingQueueTypes>('isModal'), [
        queue,
    ]);

    return (
        <Portal>
            <TransitionGroup className={styles.notifications}>
                {notifications.map((notification: NotificationType) => (
                    <TransitionComponent key={notification.id} timeout={animation * 2}>
                        <Notification
                            notification={notification}
                            hide={() => hide(notification.id)}
                        />
                    </TransitionComponent>
                ))}
            </TransitionGroup>
            <TransitionGroup>
                {modals.map((modal: ModalType) => (
                    <TransitionComponent key={modal.id} timeout={animation}>
                        <Modal modal={modal} />
                    </TransitionComponent>
                ))}
            </TransitionGroup>
        </Portal>
    );
};

export const notificationRenderer: FC<NotificationType> = ({ content }) => (
    <Text<ComponentProps<'span'>> component='span' textType='light'>
        {content}
    </Text>
);
